import { zodResolver } from '@hookform/resolvers/zod';
import { json, redirect, type ActionFunction, type HeadersFunction, type MetaFunction } from '@remix-run/node';
import { Form, useActionData, useNavigation } from '@remix-run/react';
import { useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthLeftPanel from '~/components/account/AuthLeftPanel';
import Default from '~/layouts/Default';
import { forgottenPasswordSchema, type ForgottenPasswordFormValues } from '~/schemas/forgotten-password.schema';
import { apiFetch } from '~/utils/apiFetch';
import { privatePageHeaders } from '~/utils/cacheControl.server';
import { detectLocale } from '~/utils/language';
import { getChannelDefaultLocale } from '~/utils/language.server';
import { buildPageMeta } from '~/utils/meta';
import { createRoutePath } from '~/utils/routePath';

interface ActionData {
  formErrorKey?: string;
  formError?: string;
  values?: { email: string };
}

export const action: ActionFunction = async ({ request }) => {
  const channelDefaultLocale = getChannelDefaultLocale(request);
  const locale = detectLocale(request, channelDefaultLocale);
  const routePath = createRoutePath(locale, channelDefaultLocale);

  const apiUrl = process.env.PUBLIC_API_URL;
  if (!apiUrl) {
    throw new Error('PUBLIC_API_URL is not defined');
  }

  const form = await request.formData();
  const email = form.get('email');
  if (typeof email !== 'string' || !email) {
    return json<ActionData>({ formErrorKey: 'pages.forgotten_password.errors.email_required' }, { status: 400 });
  }

  try {
    const res = await apiFetch(`${apiUrl}/api/v2/shop/customers/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const text = await res.text();

    let data: unknown = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        console.error('Failed to parse response JSON:', text);
        data = {};
      }
    }

    const isErrorResponse = (v: unknown): v is { message?: string; detail?: string } =>
      typeof v === 'object' && v !== null;

    if (!res.ok) {
      const msg =
        isErrorResponse(data) && (data.message || data.detail)
          ? data.message || data.detail
          : 'Failed to request password reset.';

      return json<ActionData>({ formError: msg, values: { email } }, { status: 400 });
    }

    return redirect(routePath('login', '?resetRequested=true'));
  } catch (e) {
    console.error('Reset password error:', e);
    return json<ActionData>({ formErrorKey: 'pages.forgotten_password.errors.unexpected_error' }, { status: 500 });
  }
};

export const meta: MetaFunction = ({ matches }) => buildPageMeta('Obnovení hesla', matches);

export const headers: HeadersFunction = ({ parentHeaders }) => privatePageHeaders(parentHeaders);

export default function ForgottenPasswordPage() {
  const actionData = useActionData<ActionData>();
  const navigation = useNavigation();
  const busy = navigation.state !== 'idle';
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgottenPasswordFormValues>({
    resolver: zodResolver(forgottenPasswordSchema),
    defaultValues: {
      email: actionData?.values?.email ?? '',
    },
  });

  const onSubmit = () => {
    formRef.current?.submit();
  };

  return (
    <Default>
      <div className="container my-auto">
        <div className="row my-4">
          <AuthLeftPanel />
          <div className="col-12 col-sm-10 offset-sm-1 col-md-8 offset-md-2 col-lg-6 offset-lg-0 col-xl-4 offset-xl-1 order-lg-1">
            <div className="d-flex justify-content-center align-items-center h-100 px-3">
              <div className="w-100 py-lg-5 mb-5 my-lg-5">
                <h1 className="h2 mb-3">{t('pages.forgotten_password.title')}</h1>
                <p className="mb-4">{t('pages.forgotten_password.description')}</p>

                {(actionData?.formErrorKey || actionData?.formError) && (
                  <div className="alert alert-danger">
                    {actionData.formErrorKey ? t(actionData.formErrorKey) : actionData.formError}
                  </div>
                )}

                <Form method="post" noValidate ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-4">
                    <label htmlFor="email" className="form-label required">
                      {t('pages.forgotten_password.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      autoComplete="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      {...register('email')}
                    />
                    {errors.email && <div className="invalid-feedback">{t(errors.email.message as string)}</div>}
                  </div>
                  <div className="d-grid">
                    <Button type="submit" disabled={busy}>
                      {busy ? t('pages.forgotten_password.submitting') : t('pages.forgotten_password.submit')}
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Default>
  );
}
